/**
 * setupStatusTier — pure helper that maps a freshness object to a
 * human-readable "what's the setup engine doing right now?" tier.
 *
 * Lives alongside LiveCoachFeed.js but extracted so it's testable in node
 * (LiveCoachFeed.js has JSX + React imports, which the .mjs test harness
 * can't load).
 *
 * Tier thresholds match the per-detector warmups inside setupPatterns.js:
 *   signal-bar:    1 bar
 *   3-bar-play:    3 bars
 *   h2 / l2:       10 bars (entryIdx >= 10 check in h2l2.js)
 *   uno-reverse:   ~10 bars
 *   holy-grail:    ~25 bars (20 EMA + ADX warmup)
 *   turtle-soup:   ~30 bars (swing pivot lookback)
 *   wolf-stack:    ~50 bars (EMA-50 warmup)
 *
 * `freshness.barCount` is today-only (the bar source slices by trading day
 * before returning), so the count maps directly to session-time-elapsed at
 * ~5 min per bar.
 *
 * Pure function. No side effects.
 *
 * @param {Object|null} freshness  computeBarFreshness output, or null when
 *                                 the first poll hasn't completed yet.
 * @returns {{key: string, title: string, detail: string}}
 */
export const setupStatusTier = (freshness) => {
  const barCount = freshness?.barCount || 0;
  // Bars × 5 min/bar gives session-time elapsed. We surface this as an ETA
  // to the next detector unlock so the trader has a sense of "when will the
  // panel start having things to say."
  const minsUntil = (target) => Math.max(0, (target - barCount) * 5);

  if (barCount === 0) {
    return {
      key: 'no-bars',
      title: 'Waiting for bars',
      detail: 'No 5m bars received for this session yet. If the market is open, check that JournifiSync NT is running.',
    };
  }
  if (barCount < 3) {
    return {
      key: 'session-start',
      title: 'Session just started',
      detail: `${barCount} 5m bar${barCount === 1 ? '' : 's'} closed so far. First setups eligible after ${3 - barCount} more (~${minsUntil(3)} min).`,
    };
  }
  if (barCount < 10) {
    return {
      key: 'warming-short',
      title: 'Setup engine warming up',
      detail: `${barCount} bars closed. Signal Bar + 3-Bar Play eligible now. H2 / L2 + Uno Reverse online in ${10 - barCount} more bars (~${minsUntil(10)} min).`,
    };
  }
  if (barCount < 25) {
    return {
      key: 'warming-mid',
      title: 'Setup engine warming up',
      detail: `${barCount} bars closed. 4 short-lookback setups eligible. Holy Grail online in ${25 - barCount} more bars (~${minsUntil(25)} min).`,
    };
  }
  if (barCount < 50) {
    return {
      key: 'warming-late',
      title: 'Almost fully armed',
      detail: `${barCount} bars closed. 6 of 7 detectors eligible. Wolf Stack online in ${50 - barCount} more bars (~${minsUntil(50)} min).`,
    };
  }
  return {
    key: 'all-armed',
    title: 'All setups armed — quiet bar',
    detail: `${barCount} bars analyzed. No actionable fires on the latest close. The engine re-checks every 5 min as new bars print.`,
  };
};

export default setupStatusTier;
